// React
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Material UI
import {
  InputAdornment,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Check, Clear, LockOutlined } from "@mui/icons-material";

// Common
import { FormikTextField } from "Common/Components";

// Utils
import {
  getPasswordRequirements,
  getPasswordStrength,
  getStrengthConfig,
} from "./passwordUtils";

const PasswordField = ({
  formik,
  name = "password",
  label,
  autoComplete = "new-password",
  extraHelperError,
}) => {
  const { t } = useTranslation();
  const password = formik.values[name] || "";
  const fieldError = formik.touched[name] ? formik.errors[name] : undefined;
  const [strengthScore, setStrengthScore] = useState(null);
  const passwordRequirements = getPasswordRequirements(password);

  const isPasswordValid =
    passwordRequirements.hasUpper &&
    passwordRequirements.hasLower &&
    passwordRequirements.hasNumberOrSpecial &&
    passwordRequirements.isMinLength;

  useEffect(() => {
    let isActive = true;
    if (!password) {
      setStrengthScore(null);
      return () => (isActive = false);
    }

    const checkPasswordStrength = async () => {
      try {
        const result = await getPasswordStrength(password);
        if (!isActive) return;
        setStrengthScore(result.score);
      } catch {
        if (!isActive) return;
        setStrengthScore(0);
      }
    };

    const timer = setTimeout(() => {
      checkPasswordStrength();
    }, 200);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [password]);

  const strengthConfig = getStrengthConfig({ strengthScore, t });
  const requirementItems = [
    {
      key: "minLength",
      met: passwordRequirements.isMinLength,
      label: t("password.minCharacters"),
    },
    {
      key: "uppercase",
      met: passwordRequirements.hasUpper,
      label: t("password.uppercase"),
    },
    {
      key: "lowercase",
      met: passwordRequirements.hasLower,
      label: t("password.lowercase"),
    },
    {
      key: "numberOrSpecial",
      met: passwordRequirements.hasNumberOrSpecial,
      label: t("password.specialOrNumber"),
    },
  ];

  const generateHelperText = () => {
    if (!password) {
      return fieldError;
    }

    return (
      <Stack>
        {extraHelperError && (
          <Typography variant="caption" color="error">
            {extraHelperError}
          </Typography>
        )}
        {!isPasswordValid && (
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Stack spacing={0.5} sx={{ width: "100%" }}>
              <LinearProgress
                variant="determinate"
                value={strengthConfig.value}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: strengthConfig.color,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: strengthConfig.color,
                  textAlign: "right",
                }}
              >
                {strengthConfig.label}
              </Typography>
            </Stack>
            {requirementItems.map(({ key, met, label }) => (
              <Stack
                key={key}
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center", justifyContent: "flex-start" }}
              >
                {met ? (
                  <Check color="success" fontSize="small" />
                ) : (
                  <Clear color="error" fontSize="small" />
                )}
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <FormikTextField
      formik={formik}
      name={name}
      field={{
        type: "password",
        label: label || t("password.label"),
        required: false,
        autoComplete,
        helperText: generateHelperText(),
        hideErrorText: Boolean(password),
        slotProps: {
          htmlInput: { "aria-required": true },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined />
              </InputAdornment>
            ),
          },
        },
      }}
    />
  );
};

export default PasswordField;
